import React, {useEffect, useMemo} from 'react';
import clsx from 'clsx';
import {useResizeDetector} from 'react-resize-detector';
import _ from 'underscore';
import {faExpandArrowsAlt} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Fade, LinearProgress, type SkeletonProps} from '@mui/material';
import {useAssetCustomThumbnail} from '@netx/core-asset-custom-thumbnail';
import {canZoomAsset, pdfLaunchStandard} from '@netx/core-asset-next/enablers';
import {type IAsset} from '@netx/core-asset-next/schema';
import {useAssetVersionProgress} from '@netx/core-asset-version/hooks/useAssetVersionProgress';
import {
	GenericImage,
	genericImageClasses,
	type GenericImageProps,
	isDefaultIcon,
} from '@netx/core-components/GenericImage';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {appRouter, dispatcher} from '@netx/core-next/instance';
import {silenceEvent} from '@netx/core-next/utils/browser';
import {useAssetTasksProgress} from '@netx/core-upload-asset/hooks/useAssetTasksProgress';
import {i18n} from '@netx/core/i18n';
import {useAssetImage} from '../../hooks/useAssetImage';
import {getPreviewTypeFromAsset} from '../../utils';
import {
	assetImageClasses,
	AssetImageClasses,
	getAssetImageUtilityClass,
} from './assetImageClasses';
import {AssetImageWorking} from './AssetImageWorking';
import {useAssetImageSize} from './useAssetImageSize';
import {type Size} from './utils';

type AssetImageUtilityClassProps = WithClasses<
	AssetImageClasses,
	{
		hideShadow?: boolean;
	}
>;

const useUtilityClasses = (ownerState: AssetImageUtilityClassProps) => {
	const {classes, hideShadow} = ownerState;
	const slots = {
		root: ['root', hideShadow && 'hideShadow'],
		progress: ['progress'],
	};
	return composeClasses(slots, getAssetImageUtilityClass, classes);
};

const AssetImageRoot = styled(GenericImage, {
	name: 'AssetImage',
	slot: 'Root',
})(({theme}) => ({
	[`&:not(.${assetImageClasses.hideShadow})`]: {
		[`& .${genericImageClasses.image}, & .${genericImageClasses.customIcon} .thumbnail`]:
			{
				boxShadow: theme.shadows[2],
			},
	},
}));

const AssetImageLaunchZoom = styled('div', {
	name: 'AssetImage',
	slot: 'LaunchZoom',
})(({theme}) => ({
	...theme.mixins.absoluteFill,
	cursor: 'pointer',
	opacity: 0,
	transition: theme.transitions.create('opacity'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	'&:hover': {
		opacity: 1,
	},
}));

const AssetImageLaunchZoomButton = styled('div', {
	name: 'AssetImage',
	slot: 'LaunchZoomButton',
})(({theme}) => ({
	background: theme.palette.custom.background.backdrop,
	borderRadius: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: 50,
	width: 50,
	color: theme.palette.primary.main,
}));

// const AssetImageProgress = styled(LinearProgress, {
// 	name: 'AssetImage',
// 	slot: 'Progress',
// })(({theme}) => ({
// 	position: 'absolute',
// 	top: '50%',
// 	left: theme.spacing(2),
// 	marginTop: -2,
// 	right: theme.spacing(2),
// }));

const AssetImageWorkingRoot = styled(AssetImageWorking)(({theme}) => ({
	display: 'flex',
	...theme.mixins.absoluteFill,
	zIndex: 4,
}));

export interface AssetImageProps
	extends
		Omit<GenericImageProps, 'classes' | 'alt' | 'src' | 'queryParams'>,
		WithClasses<
			AssetImageClasses,
			{
				className?: string;
				asset?: Pick<
					IAsset,
					| 'assetId'
					| 'file'
					| 'filetypelabel'
					| 'moddate'
					| 'name'
					| 'jwt'
					| 'previewUrl'
					| 'thumbUrl'
					| 'zoomUrl'
					| 'fileheight'
					| 'filewidth'
					| 'previewHeight'
					| 'previewWidth'
					| 'fileFormatFamily'
					| 'status'
					| 'viewNames'
				>;
				disableLoader?: boolean;
				disableFade?: boolean;
				loading?: boolean;
				forceImageSize?: Size;
				hideShadow?: boolean;
				hideTasks?: boolean;
				disableXHR?: boolean;
				queryParams?: Record<string, string>;
				showZoomAction?: boolean;
				SkeletonProps?: Partial<SkeletonProps>;
				onShowPdfViewer?: () => void;
			}
		> {}

export const AssetImagePrimitive = ({
	classes: _classes,
	className,
	asset,
	disableFade,
	disableXHR,
	forceImageSize,
	hideShadow,
	hideTasks,
	disableLoader,
	loading,
	queryParams,
	showZoomAction,
	SkeletonProps,
	onShowPdfViewer,
	...props
}: AssetImageProps) => {
	const customThumbnail = useAssetCustomThumbnail({assetId: asset?.assetId});
	const assetVersion = useAssetVersionProgress({assetId: asset?.assetId});
	const assetTasks = useAssetTasksProgress({assetId: asset?.assetId});

	const previewType = useMemo(() => getPreviewTypeFromAsset(asset), [asset]);

	const classes = useUtilityClasses({
		classes: _classes,
		hideShadow,
	});

	const _queryParams = useMemo(
		() => ({
			...queryParams,
			...customThumbnail.cacheBuster,
			...assetVersion.cacheBuster,
			...assetTasks.cacheBuster,
		}),
		[
			queryParams,
			customThumbnail.cacheBuster,
			assetVersion.cacheBuster,
			assetTasks.cacheBuster,
		],
	);

	const assetImageUrls = useAssetImage(asset, _queryParams);

	const {
		width: containerWidth = 0,
		height: containerHeight = 0,
		ref: targetRef,
	} = useResizeDetector<HTMLDivElement>({
		handleWidth: true,
		handleHeight: true,
	});

	const imageSizeDetails = useAssetImageSize({
		asset,
		containerWidth,
		containerHeight,
		forceImageSize,
	});

	const zoomable = useMemo(() => {
		if (previewType === 'document') return true;
		return canZoomAsset(asset);
	}, [asset, previewType]);

	return (
		<AssetImageRoot
			{...props}
			ref={targetRef}
			className={clsx(classes.root, className)}
			alt={asset?.name || 'asset thumbnail'}
			data-testid="asset-image"
			disableFade={disableFade}
			imageDims={imageSizeDetails.dimensions}
			loading={loading || !asset}
			queryParams={_queryParams}
			src={
				isDefaultIcon(asset?.thumbUrl)
					? asset?.thumbUrl
					: assetImageUrls[imageSizeDetails.size]
			}
			SkeletonProps={SkeletonProps}
			wrapperChildren={
				showZoomAction && zoomable ? (
					<AssetImageLaunchZoom
						onClick={(event) => {
							if (!asset) return;
							silenceEvent(event);
							if (previewType === 'document') {
								if (pdfLaunchStandard()) {
									dispatcher.trigger('asset:docview:browser:react', asset);
								} else {
									if (onShowPdfViewer) {
										onShowPdfViewer();
									} else {
										appRouter.navigate(`#document/${asset.assetId}`, true);
									}
								}
							} else {
								appRouter.navigate(`#asset/${asset.assetId}/zoom`, true);
							}
						}}
					>
						<AssetImageLaunchZoomButton>
							<FontAwesomeIcon icon={faExpandArrowsAlt} />
						</AssetImageLaunchZoomButton>
					</AssetImageLaunchZoom>
				) : undefined
			}
		>
			{/* {!disableLoader ? (
				<Fade in={imageLoading}>
					<AssetImageProgress
						className={classes.progress}
						variant={progress ? 'determinate' : 'indeterminate'}
						value={progress ? progress : undefined}
					/>
				</Fade>
			) : null} */}

			{!hideTasks ? (
				<Fade
					in={
						customThumbnail.isWorking ||
						assetVersion.isWorking ||
						assetTasks.isWorking
					}
					unmountOnExit
					mountOnEnter
				>
					<AssetImageWorkingRoot
						className={classes.progress}
						progress={
							assetTasks.isWorking
								? assetTasks.progress
								: assetVersion.isWorking
									? assetVersion.progress
									: customThumbnail.progress
						}
						primary={
							assetTasks.isWorking
								? i18n.t('i18n.processing')
								: assetVersion.isWorking
									? i18n.t('i18n.processingAssetCheckin')
									: customThumbnail.isWorking
										? i18n.t('i18n.settingCustomThumbnail')
										: undefined
						}
					/>
				</Fade>
			) : null}
		</AssetImageRoot>
	);
};

export const AssetImage = React.memo(AssetImagePrimitive) as (
	props: AssetImageProps,
) => JSX.Element;

export default AssetImage;
